import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowLeftRight, MapPin, Check, Calendar, Users, Lightbulb, Tag } from 'lucide-react';
import { tours as mockTours } from '../data/mock';
import Header from '../components/Header';
import { Footer, WhatsAppButton } from '../components/Footer';
import ReservationModal from '../components/ReservationModal';
import { useLanguage } from '../context/LanguageContext';
import { getTranslatedTour } from '../data/tourTranslations';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [tour, setTour] = useState(null);
  const [translatedTour, setTranslatedTour] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showReservation, setShowReservation] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTour = async () => {
      setLoading(true);
      try {
        // Try to fetch from API first
        const response = await axios.get(`${BACKEND_URL}/api/tours/${id}`);
        if (response.data) {
          setTour(response.data);
          setTranslatedTour(getTranslatedTour(response.data, language));
        } else {
          throw new Error('Tour not found');
        }
      } catch (err) {
        // Fallback to mock data
        const foundTour = mockTours.find(t => String(t.id) === String(id));
        if (foundTour) {
          setTour(foundTour);
          setTranslatedTour(getTranslatedTour(foundTour, language));
        } else {
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [id, navigate, language]);

  if (loading || !tour || !translatedTour) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-28">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      </>
    );
  }

  const isOnSale = tour.originalPrice && tour.originalPrice > tour.salePrice;
  const images = tour.gallery || [tour.image];

  return (
    <>
      <Header />
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-teal-500 hover:text-teal-600 mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t('backToTours')}</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Gallery */}
            <div>
              <div className="relative rounded-xl overflow-hidden mb-4">
                {isOnSale && (
                  <div className="absolute top-4 left-4 bg-orange-500 text-white text-sm font-bold px-4 py-2 rounded z-10">
                    {t('offer')}
                  </div>
                )}
                <img
                  src={images[selectedImage]}
                  alt={translatedTour.title}
                  className="w-full h-96 object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-teal-500' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-20 h-20 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {tour.locations.map((loc, i) => (
                  <span key={i} className="text-xs bg-teal-50 text-teal-600 px-3 py-1 rounded-full">
                    {loc}
                  </span>
                ))}
                {tour.tag && (
                  <span className="text-xs bg-orange-50 text-orange-600 px-3 py-1 rounded-full flex items-center gap-1">
                    <Tag size={12} />
                    {tour.tag}
                  </span>
                )}
              </div>

              <h1 className="text-3xl font-bold text-gray-800 mb-4">{translatedTour.title}</h1>

              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={18} className="text-teal-500" />
                  <span>{translatedTour.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <ArrowLeftRight size={18} className="text-teal-500" />
                  <span>{translatedTour.transport}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={18} className="text-teal-500" />
                  <span>{tour.locations.join(', ')}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed mb-6">
                {translatedTour.description}
              </p>

              {/* Price Box */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                {/* Check if tour has multiple activities */}
                {translatedTour.activities ? (
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4 text-lg">{t('chooseExperience')}</h3>
                    <div className="space-y-4">
                      {translatedTour.activities.map((activity, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-teal-500 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold text-gray-800">{activity.name}</h4>
                              <p className="text-sm text-teal-600">{activity.duration}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-bold text-teal-500">${tour.activities[index].price}</span>
                              <span className="text-gray-500 text-sm"> /{language === 'es' ? 'persona' : 'person'}</span>
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{activity.description}</p>
                          {tour.activities[index].priceFromBayahibe && (
                            <p className="text-orange-500 text-sm font-medium">
                              {language === 'es' ? 'Desde Bayahíbe' : 'From Bayahíbe'}: ${tour.activities[index].priceFromBayahibe} {language === 'es' ? 'por persona' : 'per person'}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowReservation(true)}
                      className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Calendar size={20} />
                      {t('bookNow')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end gap-3 mb-4">
                      {isOnSale && (
                        <span className="text-gray-400 line-through text-xl">${tour.originalPrice}</span>
                      )}
                      <span className="text-4xl font-bold text-teal-500">${tour.salePrice}</span>
                      {tour.priceLabel && (
                        <span className="text-gray-500">
                          {tour.priceLabel === 'por persona' ? t('perPerson') : 
                           tour.priceLabel === 'por pareja' ? t('perCouple') : 
                           tour.priceLabel === 'desde' ? t('from') : tour.priceLabel}
                        </span>
                      )}
                    </div>
                    {tour.childPrice && (
                      <p className="text-gray-600 mb-2">
                        <Users size={16} className="inline mr-2" />
                        {t('children')}: <strong>${tour.childPrice}</strong>
                      </p>
                    )}
                    {tour.extraPrice && (
                      <p className="text-orange-500 font-medium mb-4">{tour.extraPrice}</p>
                    )}
                    <button
                      onClick={() => setShowReservation(true)}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Calendar size={20} />
                      {t('bookNow')}
                    </button>
                  </>
                )}
              </div>

              {/* Includes */}
              {translatedTour.includes && (
                <div className="bg-teal-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Check size={20} className="text-teal-500" />
                    {t('whatIncludes')}
                  </h3>
                  <ul className="space-y-2">
                    {translatedTour.includes.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-600">
                        <Check size={16} className="text-teal-500 mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          {translatedTour.schedule && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Calendar size={24} className="text-teal-500" />
                {t('itinerary')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {translatedTour.schedule.map((step, index) => (
                  <div key={index} className="bg-white rounded-xl p-5 shadow-md border-l-4 border-teal-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <h4 className="font-semibold text-gray-800">{step.title}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {translatedTour.recommendations && (
            <div className="mt-12 bg-yellow-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Lightbulb size={20} className="text-yellow-500" />
                {t('recommendations')}
              </h3>
              <p className="text-gray-600">{translatedTour.recommendations}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Reservation Modal */}
      <ReservationModal
        isOpen={showReservation}
        onClose={() => setShowReservation(false)}
        tour={tour}
      />
    </>
  );
};

export default TourDetail;
